// #900 - mandatory password change: pure helpers shared by AuthStore, axios boot and the password dialog.
// The obligation is decided by the MobileProxy (signed token claim / HTTP 403); these helpers only read it.

export const PASSWORD_CHANGE_REQUIRED_CLAIM = 'pwChangeRequired'
export const PASSWORD_CHANGE_REQUIRED_RESULT = -720
export const PASSWORD_CHANGE_REQUIRED_MESSAGE = 'PASSWORD_CHANGE_REQUIRED'

function decodeBase64Url(value) {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64 + '==='.slice((base64.length + 3) % 4)
  const binary = atob(padded)
  const bytes = Uint8Array.from(binary, c => c.charCodeAt(0))
  return new TextDecoder().decode(bytes)
}

/** Payload of a JWT without verification (display only), null if not decodable. */
export function decodeTokenPayload(token) {
  if (typeof token !== 'string') {
    return null
  }
  const parts = token.split('.')
  if (parts.length < 2) {
    return null
  }
  try {
    return JSON.parse(decodeBase64Url(parts[1]))
  } catch (e) {
    return null
  }
}

/** True only if the server explicitly marked the token with a pending password change. */
export function isPasswordChangeRequiredToken(token) {
  const payload = decodeTokenPayload(token)
  return !!payload && payload[PASSWORD_CHANGE_REQUIRED_CLAIM] === true
}

/** True if a MobileProxy response rejected the call because of the pending password change. */
export function isPasswordChangeRequiredError(error) {
  const data = error && error.response && error.response.data
  if (!data || typeof data !== 'object') {
    return false
  }
  return data.iResult === PASSWORD_CHANGE_REQUIRED_RESULT || data.sErrorMsg === PASSWORD_CHANGE_REQUIRED_MESSAGE
}

const trimmed = value => (typeof value === 'string' ? value.trim() : '')

/**
 * Client-side validation of the password dialog (unchanged rules of the existing dialog).
 * @returns {{field: string, message: string}|null} null if valid
 */
export function validatePasswordChangeInput({oldPW, newPW, newPWRetype}) {
  if (trimmed(oldPW) === '') {
    return {field: 'oldPW', message: 'Bitte altes Kennwort eingeben.'}
  }
  if (trimmed(newPW) === '') {
    return {field: 'newPW', message: 'Bitte neues Kennwort eingeben.'}
  }
  if (trimmed(newPWRetype) === '') {
    return {field: 'newPWRetype', message: 'Bitte Kennwort Bestätigung eingeben.'}
  }
  if (trimmed(newPW) !== trimmed(newPWRetype)) {
    return {field: 'newPWRetype', message: 'Kennwort und Bestätigung stimmen nicht überein.'}
  }
  return null
}

/**
 * Maps a failed /changePassword call (i1_person_rtpasswordset result codes) to a dialog field error.
 * @returns {{field: string, message: string}}
 */
export function passwordChangeErrorFor(error) {
  const data = error && error.response && error.response.data
  const iResult = data && typeof data === 'object' ? data.iResult : undefined
  switch (iResult) {
    case -9:
      return {field: 'newPW', message: 'Das neue Kennwort erfüllt nicht die Sicherheitskriterien.'}
    case -13:
      return {
        field: 'newPW',
        message: 'Das neue Kennwort erfüllt nicht die Sicherheitskriterien.',
        passwordRules: Array.isArray(data.passwordRules)
          ? data.passwordRules.filter(rule => typeof rule === 'string' && rule.trim() !== '')
          : []
      }
    case -11:
      return {field: 'oldPW', message: 'Altes Kennwort ist falsch.'}
    case -14:
      return {field: 'newPW', message: 'Neues Kennwort ist zu einfach.'}
    case -15:
      return {field: 'newPWRetype', message: 'Neues Kennwort darf nicht gleich dem Alten sein.'}
    case -1:
      return {field: 'newPW', message: 'Die Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.'}
    default:
      return {field: 'newPW', message: 'Technischer Fehler beim Ändern des Kennworts.'}
  }
}
